import type { OfferRedeemMode } from '../marketing/new-client-offer/new-client-offer-enums';
import { canUseHayvenCheckIn, resolveSalonModules, type SalonModuleSources } from './resolve-salon-modules';
import type { SalonModuleStates, StoredSalonModule } from './salon-module-keys';

/**
 * What Hayven runs next to the salon's POS, as named facts every surface reads (API, web, mobile). The owner picks
 * three add-ons on Settings › How you use Hayven (Marketing is always on): Calendar & online booking, Turns, and Hayven
 * Check-in (needs Calendar or Turns). Each fact answers one question ("are bookings in Hayven?", "what proves a visit?")
 * so a surface never re-derives a meaning from the stored switches.
 *
 * Where each add-on is stored: Calendar = `Organization.disabledModules` without CALENDAR, Turns =
 * `TurnTrackerConfig.isEnabled`, Check-in = `Organization.checkInMode` HAYVEN together with `CheckInSettings.isEnabled`
 * (Hayven POS: guests always check in on Hayven). Every setup writer keeps the two in step, so a stray row with only one
 * of them reads as check-in off.
 */

export interface SalonSetupSources extends SalonModuleSources {
  /** `Organization.checkInMode` as stored (POS = guests check in on the salon's own POS). */
  checkInMode: 'HAYVEN' | 'POS';
  /** `CheckInSettings.isEnabled`; null/undefined = the salon has no check-in settings row, read like the switch off. */
  checkInEnabled: boolean | null | undefined;
}

/** What tells Hayven a client came: a paid Hayven sale, a check-in (then Done), or a counter use (Hayven Tap / sign). */
export type SalonVisitProof = 'SALE' | 'CHECK_IN' | 'COUNTER';

/** What happens to a booked visit nobody closed: auto No-show, a quiet Auto-Done, or nothing (Hayven POS). */
export type SalonUnattendedVisits = 'NO_SHOW' | 'QUIET_DONE' | 'NONE';

/** What revenue Hayven can show: real sales, the menu price of proven visits, finished turns, or none. */
export type SalonMoneySource = 'SALES' | 'MENU_ESTIMATE' | 'TURNS_ESTIMATE' | 'NONE';

/** How guests use their perks: checking in holds them, or they use them on the counter (Hayven Tap / counter sign). */
export type SalonPerkRedeem = OfferRedeemMode;

export interface SalonSetup {
  modules: SalonModuleStates;
  /**
   * Hayven Check-in is on: Calendar or Turns is on (arrivals need a place to land) and either Hayven is the POS or the
   * salon checks in on Hayven (check-in mode HAYVEN with the check-in switch on).
   */
  checkIn: boolean;
  /** The salon's bookings live in Hayven (Calendar on). Off = they live on the POS (Hayven texts the POS link). */
  bookingsInHayven: boolean;
  visitsProvedBy: SalonVisitProof;
  /**
   * Read from the switches as they are now. `NO_SHOW` only while `noShowsKnown`; elsewhere alongside the POS a booked
   * visit nobody closed closes quietly (Auto-Done, or a silent cancel for a Calendar-off kept booking check-in judged).
   * The sweeps judge each visit by the check-in window it ended in, so a visit from before a switch keeps the rule of
   * its time.
   */
  unattendedVisits: SalonUnattendedVisits;
  /** New Client Offer: `CHECK_IN` while check-in proves visits, `COUNTER_QR` otherwise. */
  perkRedeem: SalonPerkRedeem;
  /** Prize month / referral reward: `COUNTER_QR` only where a visit Done never spends perks. */
  counterPerkRedeem: SalonPerkRedeem;
  /** A completed visit spends perks (a check-in proved it, or a Hayven POS sale closed it). */
  doneUsesPerks: boolean;
  /** Hayven knows who never came: check-in proves visits and the bookings are in Hayven. */
  noShowsKnown: boolean;
  /** Hayven keeps a visit history per client (bookings, check-ins or Hayven POS sales land in Hayven). */
  visitHistoryTracked: boolean;
  money: SalonMoneySource;
  /** Turns without check-in: the desk adds walk-ins to the turn sheet itself. */
  turnSheet: boolean;
  /** Turns with check-in: clients who check in join the turn list by themselves. */
  turnsArrivals: boolean;
  /**
   * What installed apps read from the legacy `checkInMode === 'POS'` / `posCheckIn` fields: Hayven is not the POS and
   * check-in is off. Old app builds compute the add-on as `canUseHayvenCheckIn(modules) && !legacyPosCheckIn`
   * (`checkInAddOnOn`), which this keeps equal to `checkIn`.
   */
  legacyPosCheckIn: boolean;
}

/** Merges the stored switches into the salon's setup facts (pure; the API returns it as `setup`). */
export function resolveSalonSetup(sources: SalonSetupSources): SalonSetup {
  const modules = resolveSalonModules(sources);
  const hayvenPos = sources.pointOfSaleMode === 'HAYVEN_POS';
  // Alongside its own POS a salon may pick POS check-in; with Hayven as the POS guests always check in on Hayven.
  const checkIn = canUseHayvenCheckIn(modules)
    && (hayvenPos || (sources.checkInMode === 'HAYVEN' && sources.checkInEnabled === true));
  // Check-in proves who came only while its switch is on (a visit nobody checked in becomes a No-show). Alongside the
  // POS that is `checkIn` itself; a Hayven POS salon keeps its switch on wherever check-in can run.
  const checkInProves = checkIn && sources.checkInEnabled === true;
  const visitsProvedBy: SalonVisitProof = hayvenPos ? 'SALE' : checkInProves ? 'CHECK_IN' : 'COUNTER';
  const doneUsesPerks = visitsProvedBy !== 'COUNTER';
  const noShowsKnown = checkInProves && modules.calendar;

  return {
    modules,
    checkIn,
    bookingsInHayven: salonBookingsInHayven(sources.disabledModules),
    visitsProvedBy,
    // Auto-Done and auto No-show only run alongside the salon's own POS. A No-show only where Hayven knows who never
    // came: with Calendar off the salon's bookings live on its POS, so a kept booking nobody checked in closes quietly
    // (no No-show, no Missed-visit text).
    unattendedVisits: hayvenPos ? 'NONE' : noShowsKnown ? 'NO_SHOW' : 'QUIET_DONE',
    perkRedeem: checkInProves ? 'CHECK_IN' : 'COUNTER_QR',
    counterPerkRedeem: doneUsesPerks ? 'CHECK_IN' : 'COUNTER_QR',
    doneUsesPerks,
    noShowsKnown,
    // A Hayven POS sale is a visit too: those salons keep the history whatever the add-ons.
    visitHistoryTracked: hayvenPos || modules.calendar || checkIn,
    money: hayvenPos ? 'SALES' : checkIn ? 'MENU_ESTIMATE' : modules.turns ? 'TURNS_ESTIMATE' : 'NONE',
    turnSheet: modules.turns && !checkIn,
    turnsArrivals: modules.turns && checkIn,
    legacyPosCheckIn: !hayvenPos && !checkIn,
  };
}

/**
 * The Hayven Check-in add-on as installed app builds compute it from the setup (`canUseHayvenCheckIn(modules) &&
 * !legacyPosCheckIn`): always equal to `setup.checkIn`, because `legacyPosCheckIn` is defined from it. Kept so old app
 * versions and the API agree; new code reads `setup.checkIn`.
 */
export function checkInAddOnOn(setup: Pick<SalonSetup, 'modules' | 'legacyPosCheckIn'>): boolean {
  return canUseHayvenCheckIn(setup.modules) && !setup.legacyPosCheckIn;
}

/**
 * Check-in judges the salon's bookings, so Hayven knows who never came (no-shows, chairs filled, busy times): Calendar
 * on with the Check-in add-on on. The rule installed app builds compute; equal to `setup.noShowsKnown` on every row the
 * setup writers leave. New code reads `setup.noShowsKnown`.
 */
export function salonBookingsJudgedByCheckIn(
  setup: Pick<SalonSetup, 'modules' | 'legacyPosCheckIn' | 'bookingsInHayven'>,
): boolean {
  return setup.bookingsInHayven && checkInAddOnOn(setup);
}

/**
 * `setup.bookingsInHayven` from the one column it reads (Calendar = `disabledModules` without CALENDAR), for API paths
 * that already hold the org row or need only this fact (texts, pushes, the AI phone).
 */
export function salonBookingsInHayven(disabledModules: readonly StoredSalonModule[]): boolean {
  return !disabledModules.includes('CALENDAR');
}

/**
 * The salon's bookings live on its own POS: bookings not in Hayven (Calendar off) and Hayven is not the POS (a Hayven
 * sale proves its visits). What names "your POS booking link" or the counter sign keys on this one rule (Get started,
 * billing, bulk email, Google copy, the salon's screens and the agency's missing-link marks). A Hayven POS salon with
 * Calendar off has no other POS: its bookings are simply not in Hayven, and that copy would be wrong.
 */
export function salonSetupBooksOnOwnPos(setup: Pick<SalonSetup, 'bookingsInHayven' | 'visitsProvedBy'>): boolean {
  return !setup.bookingsInHayven && setup.visitsProvedBy !== 'SALE';
}

/** The three add-ons the owner picks next to Marketing (Settings › How you use Hayven). */
export interface SalonAddOns {
  calendar: boolean;
  turns: boolean;
  checkIn: boolean;
}

/** The add-ons a salon runs today, read from its setup facts. */
export function salonAddOnsOf(setup: Pick<SalonSetup, 'modules' | 'checkIn'>): SalonAddOns {
  return { calendar: setup.modules.calendar, turns: setup.modules.turns, checkIn: setup.checkIn };
}

/** Check-in needs Calendar or Turns for arrivals to land on: picking neither turns it off with them. */
export function normalizeSalonAddOns(addOns: SalonAddOns): SalonAddOns {
  return { ...addOns, checkIn: addOns.checkIn && (addOns.calendar || addOns.turns) };
}
